// CRB2 (docs/build-briefs/CRB2-custom-report-saved-reports.md; design/mockups/report-builder/01-home-franchisee and
// 04a-saved-report-export): the pure model of the Reports home and the saved report screen. No DOM and nothing node-only, so a
// plain node:test file imports it (test/crb2-ui-model.test.ts) and the browser bundle does too.
//
// What it decides: the words on the list and the tabs, which controls a person gets for a given saved report (each one resolves
// a PERMISSION, never a role name, plus the object rule the server also enforces), and the version list. The server decides
// everything for real; this only keeps a person from being offered a control the server will refuse.

import type { SavedReportDetailDto, SavedReportListDto, SavedReportSummaryDto, SavedReportTabId, SavedReportVisibility } from "./api.ts";
import { NOT_AVAILABLE } from "./customReportModel.ts";

export const SAVED_TABS: ReadonlyArray<{ id: SavedReportTabId; label: string }> = [
  { id: "all", label: "All" },
  { id: "mine", label: "Mine" },
  { id: "scheduled", label: "Scheduled" },
];

/** Counts include schedules visible to the current person. */
export function tabCount(tab: SavedReportTabId, counts: SavedReportListDto["counts"] | undefined): string {
  if (counts === undefined) return NOT_AVAILABLE;
  return String(counts[tab]);
}

/** "All 3", one name, or "2 of 3". */
export function locationsCell(summary: Pick<SavedReportSummaryDto, "locations">, permitted: number): string {
  if (summary.locations.kind === "all") return `All ${permitted}`;
  const names = summary.locations.names;
  if (names.length === 1) return names[0] ?? NOT_AVAILABLE;
  return `${names.length} of ${permitted}`;
}

/** Server summaries contain only schedules visible to the current person. */
export function scheduleCell(summary: Pick<SavedReportSummaryDto, "schedule">): string {
  if (summary.schedule === null) return NOT_AVAILABLE;
  return `${summary.schedule.label}${summary.schedule.paused ? " · Paused" : ""}`;
}

/** "Oct 14", with the year when it is not this year. `timeZone` is for tests; the browser's own zone otherwise. */
export function editedCell(iso: string, now: Date = new Date(), timeZone?: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return NOT_AVAILABLE;
  const zone = timeZone === undefined ? {} : { timeZone };
  const sameYear = at.toLocaleDateString("en-US", { ...zone, year: "numeric" }) === now.toLocaleDateString("en-US", { ...zone, year: "numeric" });
  return at.toLocaleDateString("en-US", {
    ...zone,
    month: "short",
    day: "numeric",
    ...(sameYear ? {} : { year: "numeric" }),
  });
}

/** The tag beside a shared report's name; a private one carries none. */
export function visibilityTag(visibility: SavedReportVisibility): string {
  return visibility === "shared" ? "Shared" : "";
}

export const VISIBILITY_CHOICES: ReadonlyArray<{ value: SavedReportVisibility; label: string }> = [
  { value: "private", label: "Private" },
  { value: "shared", label: "Shared" },
];

/** The menu word for changing who sees a report. */
export function shareLabel(visibility: SavedReportVisibility): string {
  return visibility === "shared" ? "Make private" : "Share";
}

type Held = (permission: string) => boolean;

/** New report: building needs report.custom.build and a dataset to build from. */
export function mayStartReport(held: Held, datasetCount: number): boolean {
  return held("report.custom.build") && datasetCount > 0;
}

/** Edit as a new version: the permission, and the object rule (the creator, or any builder when it is shared). */
export function mayEditSaved(held: Held, detail: Pick<SavedReportDetailDto, "canEdit">): boolean {
  return held("report.custom.build") && detail.canEdit;
}

/** Save a copy: any holder of the build permission who can open the report. */
export function mayCopySaved(held: Held): boolean {
  return held("report.custom.build");
}

/** Share or make private: the creator only, with the build permission. */
export function mayShareSaved(held: Held, detail: Pick<SavedReportDetailDto, "canShare">): boolean {
  return held("report.custom.build") && detail.canShare;
}

/** Archive: the same rule as an edit. */
export function mayArchiveSaved(held: Held, detail: Pick<SavedReportDetailDto, "canEdit">): boolean {
  return held("report.custom.build") && detail.canEdit;
}

/** The more menu has something in it only when at least one of its three acts is allowed. */
export function hasMoreMenu(held: Held, detail: Pick<SavedReportDetailDto, "canEdit" | "canShare">): boolean {
  return mayCopySaved(held) || mayShareSaved(held, detail) || mayArchiveSaved(held, detail);
}

/** The versions, newest first, for the picker: "Version 3, Oct 14, Dana Whitfield". The first is the current one. */
export function versionChoices(
  detail: Pick<SavedReportDetailDto, "versions">,
  now: Date = new Date(),
  timeZone?: string,
): Array<{ value: string; label: string }> {
  return detail.versions.map((v, index) => ({
    value: String(v.versionNumber),
    label: `Version ${v.versionNumber}${index === 0 ? " (current)" : ""}, ${editedCell(v.savedAt, now, timeZone)}, ${v.savedBy.name}`,
  }));
}

/** The line under a saved report's title: data, locations, edited. Shared reports say so. */
export function savedMeta(
  detail: Pick<SavedReportDetailDto, "datasetLabel" | "locations" | "editedAt" | "editedBy" | "visibility">,
  permitted: number,
  now: Date = new Date(),
  timeZone?: string,
): string[] {
  const parts = [
    detail.datasetLabel,
    locationsCell(detail, permitted),
    `Edited ${editedCell(detail.editedAt, now, timeZone)} by ${detail.editedBy.name}`,
  ];
  const tag = visibilityTag(detail.visibility);
  return tag === "" ? parts : [...parts, tag];
}
